import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport, faFilePen, faExternalLinkAlt, faSpinner, faMagnifyingGlass, faCircleCheck, faTriangleExclamation, faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import EntityTable from '../components/Listing/EntityTable';
import Select from '../components/Form/Fields/Select';
import FabGroup from '../components/FabGroup';
import ImportPurchaseOrderModal from '../components/Modal/ImportPurchaseOrderModal';
import ConfirmModal from '../components/Modal/ConfirmModal';
import { formatRelativeDate, formatFullDate } from '../utils/FormatDate';
import { useAuth } from '../context/AuthProvider';
import { API_BASE_URL } from '../api';

const API = `${API_BASE_URL}/api/v1/purchase-order-imports`;

const STATUS_MAP = {
    PENDING:   { labelKey: 'purchase_order_imports.status.pending',   bg: '#fef3c7', color: '#d97706', icon: faSpinner, spin: true },
    PROCESSED: { labelKey: 'purchase_order_imports.status.processed', bg: '#fef3c7', color: '#d97706', icon: faMagnifyingGlass },
    CONFIRMED: { labelKey: 'purchase_order_imports.status.confirmed', bg: '#dcfce7', color: '#16a34a', icon: faCircleCheck },
    FAILED:    { labelKey: 'purchase_order_imports.status.failed',    bg: '#fee2e2', color: '#dc2626', icon: faTriangleExclamation },
};

// "Mohamed EL QUCHIRI" -> "Mohamed E." — first name plus the last name's initial.
const formatUploaderName = (fullName) => {
    if (!fullName) return null;
    const [firstName, ...rest] = fullName.trim().split(/\s+/);
    const lastName = rest.join(' ');
    return lastName ? `${firstName} ${lastName[0]}.` : firstName;
};

const stringToColor = (str = '') => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    let color = '#';
    for (let i = 0; i < 3; i++) color += (`00${((hash >> (i * 8)) & 0xFF).toString(16)}`).slice(-2);
    return color;
};

const userInitials = (name = '') => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
};

const UserAvatar = ({ name }) => (
    <span style={{
        width: 24, height: 24, borderRadius: 8, flexShrink: 0,
        background: stringToColor(name || ''), color: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 10, letterSpacing: '0.02em',
    }}>
        {userInitials(name)}
    </span>
);

const StatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const s = STATUS_MAP[status] || STATUS_MAP.PENDING;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: s.bg, color: s.color,
            padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
        }}>
            <FontAwesomeIcon icon={s.icon} spin={s.spin} style={{ fontSize: 11 }} />
            {t(s.labelKey)}
        </span>
    );
};

export default function PurchaseOrderImports() {
    const { t } = useTranslation();
    const { setTitle, setTitleIcon } = usePageTitle();
    const navigate = useNavigate();
    const { hasRight } = useAuth();
    const canCreate = hasRight('BONS_CREATE');
    const canDelete = hasRight('BONS_DELETE');
    const tableRef = useRef(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [creatingManual, setCreatingManual] = useState(false);

    const handleCreateManual = async () => {
        if (creatingManual) return;
        setCreatingManual(true);
        try {
            const { data } = await axios.post(`${API}/manual`, {}, { withCredentials: true });
            navigate(`/purchase-order-imports/${data.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.message || t('purchase_order_imports.toast.create_error'));
        } finally {
            setCreatingManual(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await axios.delete(`${API}/${deleteTarget.id}`, { withCredentials: true });
            toast.success(t('purchase_order_imports.toast.deleted'));
            setDeleteTarget(null);
            tableRef.current?.reload();
        } catch (err) {
            toast.error(err?.response?.data?.message || t('purchase_order_imports.toast.delete_error'));
        } finally {
            setDeleting(false);
        }
    };

    useEffect(() => {
        setTitle(`${t('Purchase Orders')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('purchase_order_imports.subtitle')}</span>`);
        setTitleIcon('ti-file-text');
        document.title = t('Purchase Orders');
        return () => setTitleIcon(null);
    }, [setTitle, setTitleIcon, t]);

    useBreadcrumbSetup([{ label: t('Purchase Orders'), url: '/purchase-order-imports' }]);

    const columns = [
        {
            Header: '#',
            accessor: 'id',
            sortable: true,
            className: 'text-center',
            Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: '#9ca3af' }}>{row.id}</span>,
        },
        {
            Header: t('purchase_order_imports.label.file'),
            accessor: 'originalFilename',
            Cell: (row) => (
                <NavLink to={`/purchase-order-imports/${row.id}`} className="cell-name" style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                    {`BA-${row.id}`}
                    <span
                        className="cell-sub"
                        data-tooltip-id="tooltip"
                        data-tooltip-content={formatFullDate(row.createdAt)}
                    >
                        {formatRelativeDate(row.createdAt)}
                    </span>
                </NavLink>
            ),
        },
        {
            Header: '',
            accessor: 'viewFile',
            className: 'text-center',
            width: 44,
            Cell: (row) => (
                <a
                    href={`${API_BASE_URL}/api/v1/purchase-order-imports/${row.id}/file`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    title={t('purchase_order_imports.action.view_file')}
                    style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        color: '#6b7280',
                    }}
                >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                </a>
            ),
        },
        {
            Header: t('Status'),
            accessor: 'status',
            maxWidth: '160px',
            className: 'text-center',
            filterable: {
                component: Select,
                label: t('Status'),
                options: [
                    { value: '', label: 'Tous' },
                    { value: 'PENDING', label: t(STATUS_MAP.PENDING.labelKey) },
                    { value: 'PROCESSED', label: t(STATUS_MAP.PROCESSED.labelKey) },
                    { value: 'CONFIRMED', label: t(STATUS_MAP.CONFIRMED.labelKey) },
                    { value: 'FAILED', label: t(STATUS_MAP.FAILED.labelKey) },
                ],
            },
            Cell: (row) => <StatusBadge status={row.status} />,
        },
        {
            Header: t('Lines'),
            accessor: 'lineCount',
            className: 'text-center',
            Cell: (row) => (
                <span style={{
                    display: 'inline-block',
                    background: '#f3f4f6', color: '#374151',
                    padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                }}>
                    {row.lineCount}
                </span>
            ),
        },
        {
            Header: t('purchase_order_imports.column.imported_by'),
            accessor: 'uploadedByName',
            Cell: (row) => row.uploadedByName
                ? (
                    <div className="d-flex align-items-center gap-2">
                        <UserAvatar name={row.uploadedByName} />
                        <span style={{ fontSize: 13, color: '#6b7280' }}>{formatUploaderName(row.uploadedByName)}</span>
                    </div>
                )
                : <span style={{ fontSize: 13, color: '#9ca3af' }}>—</span>,
        },
        {
            Header: '',
            accessor: 'actions',
            className: 'col-actions',
            Cell: (row) => (
                <div className="cell-dropdown">
                    <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                        <FontAwesomeIcon icon={faEllipsisH} />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end">
                        <li>
                            <NavLink to={`/purchase-order-imports/${row.id}`} className="dropdown-item">
                                {t('View details')}
                            </NavLink>
                        </li>
                        {canDelete && (
                            <>
                                <li><hr className="dropdown-divider" /></li>
                                <li>
                                    <button
                                        className="dropdown-item danger"
                                        disabled={row.status === 'CONFIRMED'}
                                        title={row.status === 'CONFIRMED' ? t('purchase_order_imports.tooltip.cannot_delete_confirmed') : undefined}
                                        onClick={() => setDeleteTarget(row)}
                                    >
                                        {t('Delete')}
                                    </button>
                                </li>
                            </>
                        )}
                    </ul>
                </div>
            ),
        },
    ];

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
    // — reuses the actions column's own dropdown so both views offer identical actions.
    const actionsColumn = columns.find((c) => c.accessor === 'actions');
    const renderImportCard = (row) => (
        <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                <div style={{ alignSelf: 'flex-start' }}><StatusBadge status={row.status} /></div>

                <NavLink to={`/purchase-order-imports/${row.id}`} style={{ fontSize: 13.5, fontWeight: 600, color: '#111827', marginTop: 4 }}>
                    {`BA-${row.id}`}
                </NavLink>
                <span style={{ fontSize: 11.5, color: '#9ca3af' }}>{formatRelativeDate(row.createdAt)}</span>

                {row.uploadedByName && (
                    <div className="d-flex align-items-center gap-2" style={{ marginTop: 2 }}>
                        <UserAvatar name={row.uploadedByName} />
                        <span style={{ fontSize: 12.5, color: '#6b7280' }}>{formatUploaderName(row.uploadedByName)}</span>
                    </div>
                )}

                <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                    <span style={{
                        display: 'inline-block', background: '#f3f4f6', color: '#374151',
                        padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                    }}>
                        {row.lineCount} {t('Lines')}
                    </span>
                    {actionsColumn?.Cell(row)}
                </div>
            </div>
        </div>
    );

    const buttons = canCreate ? [
        {
            id: 'new-purchase-order-import',
            label: t('Import a purchase order'),
            icon: faFileImport,
            onClick: () => setShowImportModal(true),
        },
        {
            id: 'create-purchase-order-manual',
            label: t('purchase_order_imports.action.create_manual'),
            icon: faFilePen,
            onClick: handleCreateManual,
        },
    ] : [];

    return (
        <>
            <FabGroup buttons={buttons} />

            <ImportPurchaseOrderModal
                show={showImportModal}
                onClose={() => setShowImportModal(false)}
            />

            <EntityTable
                ref={tableRef}
                endpoint={API}
                columns={columns}
                defaultSort="createdAt"
                defaultDirection="desc"
                pageSize={20}
                emptyMessage={t('purchase_order_imports.empty')}
                searchPlaceholder={t('purchase_order_imports.search_placeholder')}
                multiSelect={true}
                enableCardView={true}
                renderCard={renderImportCard}
            />

            <ConfirmModal
                id="delete-purchase-order-import"
                show={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                isLoading={deleting}
                title={t('purchase_order_imports.delete_title')}
                description={deleteTarget ? t('purchase_order_imports.delete_confirm', { id: deleteTarget.id, count: deleteTarget.lineCount, s: deleteTarget.lineCount > 1 ? 's' : '' }) : ''}
                confirmLabel={t('Delete')}
                confirmVariant="danger"
            />
        </>
    );
}
